/**
 * SAA 2026-27 New Family Orientation Portal — 5-Quarter interactive
 * onboarding flow at /orientation.
 *
 * Q1 Registration & Legal Covenants
 * Q2 Culture · 5 Principles + Code of Excellence acknowledgements
 * Q3 Digital Environment · SSO / tech stack confirmation
 * Q4 Academic Lock · study-code issuance
 * Q5 Tuition Plan Selection (Maddox Sterling)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldCheck, ArrowRight, ArrowLeft, Check, ExternalLink, GraduationCap, BookOpen, HandCoins, Cpu, Trophy, Phone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import SignaturePad from '../components/SignaturePad';

const APPAREL_SIZES = ['YM', 'YL', 'AS', 'AM', 'AL', 'AXL'];
const MINOR_OPTIONS = [
  { id: 'art',       label: 'Art & Design' },
  { id: 'music',     label: 'Music (Vocal/Instrumental)' },
  { id: 'language',  label: 'World Language' },
  { id: 'dance',     label: 'Dance & Movement' },
  { id: 'theater',   label: 'Theater & Performance' },
  { id: 'coding',    label: 'Coding & Robotics' },
  { id: 'entrepreneurship', label: 'Entrepreneurship & NIL' },
  { id: 'film',      label: 'Film & Media Production' },
];
const TECH_STACK = [
  { id: 'saa',          label: 'SAA Academic Dashboard',       url: '/dashboard',                    icon: GraduationCap, note: 'Primary student hub · curriculum lessons, task logs & grade tracking' },
  { id: 'ixl',          label: 'IXL Skill Mastery Portal',     url: 'https://ixl.com/signin/prsvr',  icon: BookOpen,      note: 'Real-time diagnostics + math/ELA mastery + 24/7 parent assessment view' },
  { id: 'google_classroom', label: 'Google Classroom',         url: 'https://classroom.google.com',  icon: BookOpen,      note: 'Assignment hand-in + teacher communication' },
  { id: 'canva',        label: 'Canva (Creative Stack)',       url: 'https://canva.com',             icon: Cpu,           note: 'Project development · media creation · TED Talk decks' },
  { id: 'rosetta',      label: 'Rosetta Stone (Language)',     url: 'https://www.rosettastone.com',  icon: BookOpen,      note: 'Modern language track · elective credit' },
  { id: 'emergent_ai',  label: 'Socratic AI · Emergent / Gemini', url: 'https://emergent.ai',        icon: Cpu,           note: 'Research + brainstorming only · Values-principle guardrails enforced' },
];

const PRINCIPLES = [
  { key: 'P', title: 'Passion',    body: 'Obsessive drive for excellence transcending the court.' },
  { key: 'R', title: 'Resilience', body: 'Treating setbacks as essential data points for growth.' },
  { key: 'S', title: 'Sacrifice',  body: 'Prioritizing long-term legacy over short-term social convenience.' },
  { key: 'V', title: 'Values',     body: 'Uncompromising integrity in the absence of an audience.' },
  { key: 'R2',title: 'Respect',    body: 'Honoring the authority of the game, coaches, and the rigors of education.' },
];

const HONORIFICS = ['Sir', "Ma'am", 'Mr.', 'Mrs.', 'Please', 'Thank You', 'I Apologize', 'Excuse Me'];

const FIVE_STAR_ROADMAP = [
  { q: 'Q1 · Fall',   title: 'Identity & Mindset',    deliverable: "The 'My Why' TED Talk",     focus: 'Self-exploration, prioritization, mental armor. Personal presentation establishing core values + public speaking baseline.' },
  { q: 'Q2 · Winter', title: 'Strategy & Leadership', deliverable: 'Podcast & Media Training',  focus: 'Executive function + narrative control. Professional podcast + on-camera training, structured post-game interviews.' },
  { q: 'Q3 · Spring', title: 'Intellect & Business',  deliverable: 'Sports Business & NIL Plan',focus: 'Financial literacy + personal brand. Contract analysis, NIL monetization strategies, marketing.' },
  { q: 'Q4 · Summer', title: 'Culmination & Edge',    deliverable: 'Recruiting Capstone Film',  focus: 'Psychological edge + marketplace readiness. Documentary short capturing athletic growth, stats, and character.' },
];

const ORIENTATION_DAY_SCHEDULE = [
  { time: '04:00 – 04:30 PM', block: 'Q1 · Registration & Gear Distribution', obj: 'Verify checklist items, process pending $650 fees, distribute Player Backpacks + uniforms + SAA curriculum materials.' },
  { time: '04:30 – 05:00 PM', block: 'Q2 · Baseline Diagnostics',              obj: 'Initial diagnostic testing. Benchmarks cognitive placement + defines athletic target metrics.' },
  { time: '05:00 – 05:30 PM', block: 'Q3 · SAA Digital Onboarding',            obj: 'Onboard devices to Google Classroom · IXL · Canva · Rosetta Stone · Emergent AI / Gemini. Setup parent tracking portal.' },
  { time: '05:30 – 06:00 PM', block: 'Q4 · The Culture Covenant Session',     obj: "Introduce SAA's Vocabulary of Honor (Sir/Ma'am). Review campus device ban + athletic consequences for dress infractions." },
];

const PRE_SEASON_CHECKLIST = [
  'Enrollment Agreement — signed, with initials on Academic Disclaimer + Withdrawal',
  'Tuition & Payment Agreement — auto-billing configured ($895/mo · $100 late · $35 NSF)',
  'Parent/Guardian Profile — primary + secondary + emergency contacts',
  'Student-Athlete Profile — academic history + primary sport + goals',
  'Medical Treatment Authorization + insurance card on file',
  'Liability & Facilities Waiver — assumption of risk + CA Civil Code §1542',
  'Media Release Option — public highlight OR internal only',
  'Academic Records Release — FERPA authorization for transcript coordination',
  'Student & Parent Expectations Agreement — behavior, dress, radical accountability',
  'Technology & Academic Integrity Agreement — device ban + Socratic AI policy',
  '$650 Enrollment Fee Cleared — covers backpack, uniform, notebook, licensing',
  'Uniform Sizing Confirmed — YM · YL · AS · AM · AL · AXL',
];

const STEPS = [
  { id: 'q1', title: 'Q1 · Registration & Legal', icon: HandCoins,   accent: '#F97316' },
  { id: 'q2', title: 'Q2 · Culture Covenant',      icon: ShieldCheck, accent: '#C9A961' },
  { id: 'q3', title: 'Q3 · Tech Environment',      icon: Cpu,         accent: '#22D3EE' },
  { id: 'q4', title: 'Q4 · Academic Lock',         icon: Trophy,      accent: '#10B981' },
  { id: 'q5', title: 'Q5 · Tuition Plans',        icon: HandCoins,   accent: '#A78BFA' },
  { id: 'q6', title: 'Q6 · School of Record',     icon: GraduationCap, accent: '#FFC72C' },
];

const emptySig = { signature_data_url: '', signer_name: '' };

// -------- EMAIL GATE --------
const EmailGate = ({ onUnlock }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!email || !email.includes('@')) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/academy/orientation-portal/lookup', { email });
      onUnlock(email, res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Lookup failed — please try again.');
    } finally { setLoading(false); }
  };

  // Auto-open when `?email=…` is present in the URL — used for founder-shared
  // direct links (e.g. sending the Singletary family a one-tap portal link).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const seeded = (params.get('email') || '').trim().toLowerCase();
    if (!seeded || !seeded.includes('@')) return;
    setEmail(seeded);
    (async () => {
      setLoading(true);
      try {
        const res = await api.post('/academy/orientation-portal/lookup', { email: seeded });
        onUnlock(seeded, res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Lookup failed — please try again.');
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4" data-testid="orientation-gate">
      <div className="max-w-md w-full">
        <div className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color: '#F97316' }}>Scholar Athlete Academy</div>
        <h1 className="text-3xl md:text-4xl font-black mt-2">Your Orientation Portal</h1>
        <p className="text-sm text-zinc-400 leading-relaxed mt-3">
          Enter the email on file with your enrollment to unlock your 2026-27 New Family Orientation.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <label className="block">
              <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase mb-1">Parent / Guardian Email</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(e); }}
                autoFocus
                placeholder="you@example.com"
                className="w-full bg-black border-2 border-zinc-800 focus:border-amber-500 outline-none p-3 text-base text-white"
                data-testid="gate-email-input"
              />
            </label>
          </div>

          {error && <div className="text-[11px] text-red-400" data-testid="gate-error">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-black text-sm tracking-widest uppercase px-4 py-3 disabled:opacity-50"
            data-testid="gate-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Enter My Orientation
          </button>
        </form>

        <div className="mt-6 text-[10px] text-zinc-500 leading-relaxed border-t border-zinc-900 pt-4">
          <strong className="text-zinc-400">First time here?</strong> Enter the email you used on your enrollment.
          If you haven&rsquo;t enrolled yet, please contact <a href="mailto:margaret@prsvr.com" className="text-amber-400 hover:text-amber-300">margaret@prsvr.com</a> to receive your enrollment link before starting orientation.
        </div>
      </div>
    </div>
  );
};

// -------- COMPLETE SCREEN --------
const AlreadyCompleteScreen = ({ submission, onStartOver }) => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center px-4" data-testid="orientation-complete-screen">
    <div className="max-w-md w-full text-center">
      <div className="text-[10px] font-black tracking-[0.32em] uppercase text-emerald-400">Orientation Complete</div>
      <h1 className="text-3xl md:text-4xl font-black mt-2 text-white">You&rsquo;re All Set</h1>
      <p className="text-sm text-zinc-400 leading-relaxed mt-3">
        We received your New Family Orientation on <span className="text-white">{new Date(submission.created_at).toLocaleDateString()}</span>.
      </p>
      {submission.study_code && (
        <div className="mt-6">
          <div className="text-[9px] font-black tracking-widest text-emerald-400 uppercase mb-1">Your Curriculum Block Study Code</div>
          <div className="text-2xl md:text-3xl font-black tracking-widest border-2 border-emerald-500 bg-emerald-500/5 p-4" data-testid="complete-study-code">{submission.study_code}</div>
          <div className="text-[10px] text-zinc-500 mt-2">Paste this into the SAA Student Dashboard to unlock your curriculum block.</div>
        </div>
      )}
      <button onClick={onStartOver} className="mt-6 text-[10px] font-black tracking-widest text-zinc-500 hover:text-white" data-testid="orientation-back-to-gate">← USE A DIFFERENT EMAIL</button>
    </div>
  </div>
);


const Orientation = () => {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Email gate state
  const [gateUnlocked, setGateUnlocked] = useState(false);
  const [completedSubmission, setCompletedSubmission] = useState(null);
  const [isStaffPreview, setIsStaffPreview] = useState(false);

  // Emergency contacts — up to 3
  const [ecList, setEcList] = useState([{ name: '', relationship: '', phone: '' }]);

  const [form, setForm] = useState({
    student_first_name: '', student_last_name: '', student_dob: '', student_gender: '', grade: '', apparel_size: '',
    parent_first_name: '', parent_last_name: '', parent_email: '', parent_phone: '',
    parent_relationship: 'Mother', parent_address: '',
    medical_conditions: '', allergies: '', insurance_provider: '', insurance_policy: '',
    physician_name: '', physician_phone: '',
    initials_650_fee: '', initials_30_day_withdrawal: '', initials_civil_1542_waiver: '', initials_academic_disclaimer: '', initials_tuition_agreement: '',
    media_release_choice: 'public_highlight',
    parent_signature: { ...emptySig },
    student_signature: { ...emptySig },
    enrollment_fee_status: 'pending', enrollment_fee_receipt_ref: '',
    culture_vocab_ack: false, device_policy_ack: false, principles_ack: false,
    minor_concentration: '',
    tech_stack_confirmed: [],
    all_forms_signed_ack: false,
    family_key: null,
    siblings: [],
    // Q6 · School of Record — final step choice
    solo_school_of_record: null,       // true | false | null (not yet chosen)
    solo_school_of_record_signed_at: null,
  });

  const [familyTuition, setFamilyTuition] = useState(null); // { family_key, family_label, students: [...] }
  const [studentSelections, setStudentSelections] = useState({}); // student_key -> plan_key
  const [tuition, setTuition] = useState({ student_name: 'Maddox Sterling', parent_email: '', plan: '', billing_contact_email: '' });
  const [tuitionSubmitting, setTuitionSubmitting] = useState(false);
  const [tuitionResult, setTuitionResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/academy/orientation-portal/config'); setConfig(r.data); }
      catch { /* silently fall through — form still works */ }
    })();
  }, []);

  const handleGateUnlock = (email, lookup) => {
    // Prefill form with anything we know about the family
    const p = lookup?.prefill || {};
    const siblings = lookup?.siblings || [];
    const familyKey = lookup?.family_key || null;

    // Normalize each sibling with the full sibling shape so the UI can bind directly
    const normalizedSiblings = siblings.map((s) => ({
      student_first_name: s.student_first_name || '',
      student_last_name:  s.student_last_name  || '',
      student_dob:        s.student_dob        || '',
      grade:              s.grade              || '',
      student_gender:     s.student_gender     || '',
      apparel_size:       s.apparel_size       || '',
      media_release_choice: s.media_release_choice || 'public_highlight',
      minor_concentration:  s.minor_concentration  || '',
    }));

    setForm((prev) => ({
      ...prev,
      ...Object.fromEntries(Object.entries(p).filter(([_, v]) => v !== undefined && v !== null && v !== '')),
      parent_email: email,
      family_key: familyKey,
      siblings: normalizedSiblings,
    }));
    // Store per-family tuition map (drives Q5 rendering)
    if (lookup?.family_tuition) {
      setFamilyTuition(lookup.family_tuition);
    }
    // Prefill tuition contact email too
    setTuition((t) => ({ ...t, parent_email: email, billing_contact_email: email }));
    // If they already finished orientation, show the "you're all set" screen instead of the form
    // (except staff — Margaret should always land in the portal to preview/help families)
    const isStaff = Boolean(lookup?.is_staff_preview);
    if (!isStaff && lookup?.existing_submission?.status === 'complete') {
      setCompletedSubmission(lookup.existing_submission);
    }
    setIsStaffPreview(isStaff);
    setGateUnlocked(true);

    // Support deep-link into a specific step via `?step=…`
    //   ?step=q5 | ?step=tuition  → jump straight to Q5 (Tuition Plans)
    //   ?step=q1..q4              → jump to that quarter
    try {
      const params = new URLSearchParams(window.location.search);
      const stepParam = (params.get('step') || '').trim().toLowerCase();
      const stepMap = { q1: 0, q2: 1, q3: 2, q4: 3, q5: 4, tuition: 4, pay: 4, payment: 4 };
      if (stepParam && stepMap[stepParam] !== undefined) {
        setStep(stepMap[stepParam]);
      }
    } catch { /* ignore */ }
  };

  const handleStartOver = () => {
    setCompletedSubmission(null);
    setGateUnlocked(false);
    setIsStaffPreview(false);
    setStep(0);
    setResult(null);
  };

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateSig = (which, patch) => setForm((prev) => ({ ...prev, [which]: { ...prev[which], ...patch } }));

  // Validate current step before advancing
  const canAdvance = useMemo(() => {
    if (step === 0) {
      return form.student_first_name && form.student_last_name && form.student_dob && form.grade && form.apparel_size
        && form.parent_first_name && form.parent_last_name && form.parent_email && form.parent_phone && form.parent_address
        && form.initials_650_fee && form.initials_30_day_withdrawal && form.initials_civil_1542_waiver
        && form.initials_academic_disclaimer && form.initials_tuition_agreement
        && form.parent_signature.signature_data_url && form.parent_signature.signer_name;
    }
    if (step === 1) return form.culture_vocab_ack && form.device_policy_ack && form.principles_ack;
    if (step === 2) return form.tech_stack_confirmed.length >= 4; // SAA + IXL + Google Classroom + one more
    if (step === 3) return form.all_forms_signed_ack;
    return true;
  }, [step, form]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, emergency_contacts: ecList.filter((e) => e.name && e.phone) };
      // student signature is optional — strip empty
      if (!payload.student_signature.signature_data_url) delete payload.student_signature;
      const res = await api.post('/academy/orientation-portal/submit', payload);
      setResult(res.data);
      toast.success(res.data.study_code ? `Study Code issued: ${res.data.study_code}` : 'Orientation submitted');
      setStep(4); // advance to Q5 Maddox tuition
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Submission failed';
      toast.error(typeof msg === 'string' ? msg : 'Please complete all required fields');
    } finally { setSubmitting(false); }
  };

  const submitTuition = async () => {
    if (!tuition.plan) { toast.error('Please choose a plan'); return; }
    if (!tuition.parent_email) { toast.error('Parent email is required for the ledger'); return; }
    setTuitionSubmitting(true);
    try {
      const res = await api.post('/academy/orientation-portal/tuition', tuition);
      setTuitionResult(res.data);
      toast.success('Tuition plan recorded · invoice queued in ops');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record plan');
    } finally { setTuitionSubmitting(false); }
  };

  // Multi-student: record a plan choice for one specific student in the family
  const submitStudentTuition = async (studentName, planKey) => {
    if (!planKey) { toast.error('Please choose a plan first'); return null; }
    const email = tuition.parent_email || form.parent_email;
    if (!email) { toast.error('Parent email is required for the ledger'); return null; }
    try {
      const res = await api.post('/academy/orientation-portal/tuition', {
        student_name: studentName,
        parent_email: email,
        plan: planKey,
        billing_contact_email: tuition.billing_contact_email || email,
      });
      toast.success(`${studentName} · plan recorded`);
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to record plan for ${studentName}`);
      return null;
    }
  };

  const updateSibling = (idx, patch) => setForm((prev) => {
    const next = [...prev.siblings];
    next[idx] = { ...next[idx], ...patch };
    return { ...prev, siblings: next };
  });

  const setInitial = (field, value) => update({ [field]: value.slice(0, 5).toUpperCase() });

  return (
    <>
      {!gateUnlocked && <EmailGate onUnlock={handleGateUnlock} />}
      {gateUnlocked && completedSubmission && <AlreadyCompleteScreen submission={completedSubmission} onStartOver={handleStartOver} />}
      {gateUnlocked && !completedSubmission && (
        <OrientationPortalBody
          config={config}
          isStaffPreview={isStaffPreview}
          step={step}
          setStep={setStep}
          submitting={submitting}
          result={result}
          form={form}
          update={update}
          updateSig={updateSig}
          updateSibling={updateSibling}
          ecList={ecList}
          setEcList={setEcList}
          tuition={tuition}
          setTuition={setTuition}
          tuitionSubmitting={tuitionSubmitting}
          tuitionResult={tuitionResult}
          familyTuition={familyTuition}
          studentSelections={studentSelections}
          setStudentSelections={setStudentSelections}
          submitStudentTuition={submitStudentTuition}
          canAdvance={canAdvance}
          setInitial={setInitial}
          submit={submit}
          submitTuition={submitTuition}
          onSwitchEmail={handleStartOver}
        />
      )}
    </>
  );
};

// -------- MAIN PORTAL BODY --------
const OrientationPortalBody = ({
  config, isStaffPreview, step, setStep, submitting, result, form, update, updateSig, updateSibling, ecList, setEcList,
  tuition, setTuition, tuitionSubmitting, tuitionResult, familyTuition, studentSelections, setStudentSelections, submitStudentTuition,
  canAdvance, setInitial, submit, submitTuition, onSwitchEmail,
}) => {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="orientation-portal">
      {isStaffPreview && (
        <div className="bg-purple-500/20 border-b-2 border-purple-500 text-center py-2 text-[10px] font-black tracking-widest uppercase text-purple-300" data-testid="staff-preview-banner">
          Staff Preview Mode · Margaret · Any submission from this session is for review only
        </div>
      )}
      {/* Header */}
      <div className="border-b-2 border-zinc-900">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color: '#F97316' }}>Scholar Athlete Academy</div>
          <h1 className="text-2xl md:text-4xl font-black mt-1">New Family Orientation Portal</h1>
          <div className="text-[11px] text-zinc-400 mt-2">
            {config?.current_year_session || '2026-2027 Academic Year'} · {config?.location_station || 'Handle Life Academy · On-Site Campus'}
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="border-b-2 border-zinc-900 sticky top-0 bg-black z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex gap-2 overflow-x-auto" data-testid="orientation-stepper">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStep(i)}
                className={`flex-shrink-0 inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] font-black tracking-widest uppercase transition-colors ${active ? 'text-black' : done ? 'text-white border-zinc-700 hover:border-white' : 'text-zinc-500 border-zinc-800 hover:text-white hover:border-zinc-600'}`}
                style={active ? { backgroundColor: s.accent, borderColor: s.accent } : {}}
                data-testid={`step-btn-${s.id}`}
              >
                <Icon className="w-3 h-3" /> {s.title}{done && <Check className="w-3 h-3 text-emerald-400 ml-1" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* --------------- Q1 --------------- */}
        {step === 0 && (
          <div className="space-y-6" data-testid="q1-content">
            <SectionHeader color="#F97316" eyebrow="Quarter 1" title="Registration & Legal Covenants" desc="The Pre-Season Checklist. Ten legal covenants, all signed digitally right here." />

            {/* Orientation Day Schedule preview */}
            <Card title="Orientation Day · 4-Quarter Schedule">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-2">Here&rsquo;s how orientation day flows once you&rsquo;re on campus at Handle Life Academy.</div>
              <div className="space-y-1.5">
                {ORIENTATION_DAY_SCHEDULE.map((s) => (
                  <div key={s.time} className="grid grid-cols-[130px_1fr] gap-2 border border-zinc-900 p-2 bg-black/40">
                    <div className="text-[10px] font-black tracking-widest text-amber-400 uppercase">{s.time}</div>
                    <div>
                      <div className="text-xs font-black text-white">{s.block}</div>
                      <div className="text-[10px] text-zinc-400 leading-snug mt-0.5">{s.obj}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Pre-Season checklist preview */}
            <Card title="Pre-Season Checklist · What Must Be Submitted">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-2">The 12 mandatory submissions — most are captured right here in this portal.</div>
              <ol className="grid md:grid-cols-2 gap-x-4 text-[11px] text-zinc-300 list-decimal list-inside space-y-0.5">
                {PRE_SEASON_CHECKLIST.map((item, i) => <li key={i} className="leading-snug">{item}</li>)}
              </ol>
            </Card>

            {/* Student profile */}
            <Card title="Student Profile & Diagnostics">
              <div className="grid md:grid-cols-2 gap-3">
                <Input label="First Name *" value={form.student_first_name} onChange={(v) => update({ student_first_name: v })} test="student-first" />
                <Input label="Last Name *"  value={form.student_last_name}  onChange={(v) => update({ student_last_name: v })}  test="student-last" />
                <Input label="Date of Birth *" type="date" value={form.student_dob} onChange={(v) => update({ student_dob: v })} test="student-dob" />
                <Select label="Gender" value={form.student_gender} onChange={(v) => update({ student_gender: v })} options={['', 'Male', 'Female', 'Non-Binary', 'Prefer Not to Say']} />
                <Input label="Grade (2026-27) *" value={form.grade} onChange={(v) => update({ grade: v })} placeholder="e.g. 6" test="student-grade" />
                <Select label="Apparel Size *" value={form.apparel_size} onChange={(v) => update({ apparel_size: v })} options={['', ...APPAREL_SIZES]} test="apparel-size" />
              </div>
            </Card>

            {/* Sibling students (multi-student family) */}
            {form.siblings && form.siblings.length > 0 && form.siblings.map((s, i) => (
              <Card key={`sib-${i}`} title={`Additional Student · ${s.student_first_name || `#${i+2}`} ${s.student_last_name || ''}`.trim()}>
                <div className="grid md:grid-cols-2 gap-3">
                  <Input label="First Name *" value={s.student_first_name} onChange={(v) => updateSibling(i, { student_first_name: v })} test={`sibling-${i}-first`} />
                  <Input label="Last Name *"  value={s.student_last_name}  onChange={(v) => updateSibling(i, { student_last_name: v })}  test={`sibling-${i}-last`} />
                  <Input label="Date of Birth *" type="date" value={s.student_dob} onChange={(v) => updateSibling(i, { student_dob: v })} test={`sibling-${i}-dob`} />
                  <Select label="Gender" value={s.student_gender} onChange={(v) => updateSibling(i, { student_gender: v })} options={['', 'Male', 'Female', 'Non-Binary', 'Prefer Not to Say']} />
                  <Input label="Grade (2026-27) *" value={s.grade} onChange={(v) => updateSibling(i, { grade: v })} placeholder="e.g. 6" test={`sibling-${i}-grade`} />
                  <Select label="Apparel Size *" value={s.apparel_size} onChange={(v) => updateSibling(i, { apparel_size: v })} options={['', ...APPAREL_SIZES]} test={`sibling-${i}-apparel`} />
                </div>
              </Card>
            ))}
            <Card title="Parent / Guardian Profile">
              <div className="grid md:grid-cols-2 gap-3">
                <Input label="First Name *" value={form.parent_first_name} onChange={(v) => update({ parent_first_name: v })} test="parent-first" />
                <Input label="Last Name *"  value={form.parent_last_name}  onChange={(v) => update({ parent_last_name: v })}  test="parent-last" />
                <Input label="Email *" type="email" value={form.parent_email} onChange={(v) => update({ parent_email: v })} test="parent-email" />
                <Input label="Phone *" value={form.parent_phone} onChange={(v) => update({ parent_phone: v })} test="parent-phone" />
                <Select label="Relationship *" value={form.parent_relationship} onChange={(v) => update({ parent_relationship: v })} options={['Mother', 'Father', 'Guardian', 'Grandparent', 'Other']} />
                <Input label="Home Address *" value={form.parent_address} onChange={(v) => update({ parent_address: v })} test="parent-address" />
              </div>
            </Card>

            {/* Emergency contacts */}
            <Card title="Emergency Pickup Contacts (up to 3)">
              {ecList.map((ec, i) => (
                <div key={i} className="grid md:grid-cols-3 gap-2 mb-2">
                  <Input label="Name" value={ec.name} onChange={(v) => setEcList((L) => L.map((x, xi) => xi === i ? { ...x, name: v } : x))} />
                  <Input label="Relationship" value={ec.relationship} onChange={(v) => setEcList((L) => L.map((x, xi) => xi === i ? { ...x, relationship: v } : x))} />
                  <Input label="Phone" value={ec.phone} onChange={(v) => setEcList((L) => L.map((x, xi) => xi === i ? { ...x, phone: v } : x))} />
                </div>
              ))}
              {ecList.length < 3 && (
                <button type="button" onClick={() => setEcList((L) => [...L, { name: '', relationship: '', phone: '' }])} className="text-[10px] font-black tracking-widest text-amber-400 mt-1" data-testid="add-emergency-contact">
                  + ADD ANOTHER CONTACT
                </button>
              )}
            </Card>

            {/* Medical & insurance */}
            <Card title="Medical & Insurance">
              <div className="grid md:grid-cols-2 gap-3">
                <Input label="Insurance Provider" value={form.insurance_provider} onChange={(v) => update({ insurance_provider: v })} />
                <Input label="Policy #"           value={form.insurance_policy}   onChange={(v) => update({ insurance_policy: v })} />
                <Input label="Physician Name"     value={form.physician_name}     onChange={(v) => update({ physician_name: v })} />
                <Input label="Physician Phone"    value={form.physician_phone}    onChange={(v) => update({ physician_phone: v })} />
              </div>
              <TextArea label="Medical Conditions (asthma, seizures, etc.)" value={form.medical_conditions} onChange={(v) => update({ medical_conditions: v })} />
              <TextArea label="Allergies"                                    value={form.allergies}         onChange={(v) => update({ allergies: v })} />
            </Card>

            {/* Media Release */}
            <Card title="Media Release · Choose One">
              <div className="grid md:grid-cols-2 gap-3">
                {['public_highlight', 'internal_only'].map((c) => (
                  <button key={c} type="button" onClick={() => update({ media_release_choice: c })}
                          className={`text-left border-2 p-3 ${form.media_release_choice === c ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-800'}`}
                          data-testid={`media-${c}`}>
                    <div className="text-xs font-black text-white">{c === 'public_highlight' ? 'PUBLIC HIGHLIGHT' : 'INTERNAL ONLY'}</div>
                    <div className="text-[10px] text-zinc-400 mt-1 leading-relaxed">
                      {c === 'public_highlight'
                        ? 'Approve use of student likeness in SAA marketing, highlight reels, and social media.'
                        : 'Restrict use to internal SAA reports & family-facing progress dashboards only. No external marketing.'}
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* 10 Covenants + Initials */}
            <Card title="The Ten Covenants · Initial Each Box (parent)">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                Please read each covenant and enter your initials in the box next to it. Full policy documents were sent with your enrollment packet; brief summaries below.
              </div>
              <CovenantInitial n={1} title="Tier 3 Full Academic Development · $650 Non-Refundable Enrollment Fee"
                body="I acknowledge the $650.00 one-time non-refundable enrollment fee (covering my student's Player Backpack, uniform polo, student playbook, and licensing). Tuition is $895.00/mo due on the 1st and reserves my student's placement regardless of attendance, injury, holidays, or travel. Program operates in person at Handle Life Academy · Mon-Thu 12:30–3:30 PM Core Block + Friday 9:00–12:00 Leadership Lab."
                initials={form.initials_650_fee} onChange={(v) => setInitial('initials_650_fee', v)} test="init-650"
                image={{ src: '/saa-uniform-polo.jpg', caption: 'Included: SAA Official Polo' }} />
              <CovenantInitial n={2} title="30-Day Written Withdrawal Notice"
                body="I understand a minimum of 30 days' written notice via margaret@prsvr.com is required for withdrawal. Failure to provide this notice results in the immediate forfeiture of the subsequent month's tuition. Mid-month withdrawal is pro-rated into the next billing cycle."
                initials={form.initials_30_day_withdrawal} onChange={(v) => setInitial('initials_30_day_withdrawal', v)} test="init-30day" />
              <CovenantInitial n={3} title="CA Civil Code § 1542 Waiver (Liability + Assumption of Risk)"
                body='I explicitly waive all rights under CA Civil Code § 1542: "A general release does not extend to claims that the creditor or releasing party does not know or suspect to exist in his or her favor at the time of executing the release and that, if known by him or her, would have materially affected his or her settlement with the debtor or released party." I voluntarily assume all inherent risks of elite athletic training, strength & conditioning, and on-court activity — including strains, sprains, fractures, concussion, and heat exhaustion.'
                initials={form.initials_civil_1542_waiver} onChange={(v) => setInitial('initials_civil_1542_waiver', v)} test="init-1542" />
              <CovenantInitial n={4} title="Academic Disclaimer · Dual-Track"
                body="The PRSVR School operates as a California state-sanctioned private school under Education Code § 49068. However, SAA/PRSVR does NOT guarantee NCAA eligibility, automatic admission to specific private preparatory high schools, or collegiate athletic recruitment outcomes. Maintaining final eligibility and meeting requirements for external academic or athletic organizations remains the sole responsibility of the student and parent."
                initials={form.initials_academic_disclaimer} onChange={(v) => setInitial('initials_academic_disclaimer', v)} test="init-academic" />
              <CovenantInitial n={5} title="Tuition Agreement · $895/mo · Late $100 · NSF $35 · 2-Month Suspension"
                body="Tuition is $895.00/mo due on the 1st. A flat $100.00 late fee is automatically assessed for any payment not received by the due date. Returned payment/NSF fee is $35.00. Enrollment and physical access to all instructional services will be automatically terminated if the account falls two consecutive payments behind. No student-athlete may attend on-site classes while their account is in active suspension."
                initials={form.initials_tuition_agreement} onChange={(v) => setInitial('initials_tuition_agreement', v)} test="init-tuition" />

              <div className="text-[10px] text-zinc-500 leading-relaxed mt-3 border border-zinc-900 p-2">
                Covenants 6-10 (Parent/Guardian Contact Registry · Student Profile &amp; Medical Auth · Emergency Treatment &amp; Insurance · Media Release · FERPA Records Release · Student &amp; Parent Expectations · Technology &amp; Academic Integrity + Socratic AI policy) are captured by the fields above and by your signature below. Full verbatim text is stored with your submission packet and emailed to margaret@prsvr.com.
              </div>
            </Card>

            {/* Enrollment fee receipt */}
            <Card title="$650 Enrollment Fee">
              <div className="text-[11px] text-zinc-400 mb-2 leading-relaxed">
                If you&rsquo;ve already paid the $650 non-refundable fee, drop your Stripe receipt or payment reference below.
                Otherwise leave this blank and Margaret will send you the payment link after review.
              </div>
              <Input label="Stripe Receipt / Payment Reference (optional)" value={form.enrollment_fee_receipt_ref}
                     onChange={(v) => update({ enrollment_fee_receipt_ref: v, enrollment_fee_status: v ? 'receipt_provided' : 'pending' })} />
            </Card>

            {/* Signatures */}
            <Card title="Digital Signatures">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Input label="Parent / Guardian Name (typed) *" value={form.parent_signature.signer_name} onChange={(v) => updateSig('parent_signature', { signer_name: v })} test="parent-sig-name" />
                  <div className="mt-2">
                    <SignaturePad label="Parent Signature *" testId="parent-sig" onChange={(url) => updateSig('parent_signature', { signature_data_url: url })} />
                  </div>
                </div>
                <div>
                  <Input label="Student-Athlete Name (typed, optional)" value={form.student_signature.signer_name} onChange={(v) => updateSig('student_signature', { signer_name: v })} />
                  <div className="mt-2">
                    <SignaturePad label="Student Signature (optional)" testId="student-sig" onChange={(url) => updateSig('student_signature', { signature_data_url: url })} />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* --------------- Q2 --------------- */}
        {step === 1 && (
          <div className="space-y-6" data-testid="q2-content">
            <SectionHeader color="#C9A961" eyebrow="Quarter 2" title="Baseline Diagnostics & Culture Covenant" desc="The 5 Principles of Perseverance + the Locker Room Code of Excellence." />
            <Card title="The 5 Principles of Perseverance">
              <div className="grid md:grid-cols-5 gap-2">
                {PRINCIPLES.map((p) => (
                  <div key={p.title} className="border-2 border-amber-500/40 bg-amber-500/5 p-2.5">
                    <div className="text-3xl font-black" style={{ color: '#C9A961' }}>{p.key}</div>
                    <div className="text-xs font-black text-white mt-1">{p.title.toUpperCase()}</div>
                    <div className="text-[10px] text-zinc-400 leading-snug mt-1">{p.body}</div>
                  </div>
                ))}
              </div>
              <Checkbox label="I have reviewed the 5 Principles of Perseverance and will hold my student to them." value={form.principles_ack} onChange={(v) => update({ principles_ack: v })} test="ack-principles" />
            </Card>

            <Card title="Vocabulary of Honor · The Locker Room Code">
              <div className="text-[11px] text-zinc-400 mb-2 leading-relaxed">
                On campus, at practice, in the classroom, and on the road, our students speak with respect. Every teacher, coach, staff member, and adult is addressed with the following honorifics.
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {HONORIFICS.map((h) => <span key={h} className="text-[11px] font-black text-white border border-amber-500/50 bg-amber-500/10 px-2 py-1">{h}</span>)}
              </div>
              <Checkbox label={"I acknowledge my student will use the vocabulary of honor (Sir · Ma'am · Mr. · Mrs. · Please · Thank You · I Apologize · Excuse Me) with all adults on campus."} value={form.culture_vocab_ack} onChange={(v) => update({ culture_vocab_ack: v })} test="ack-vocab" />
            </Card>

            <Card title="Device Policy · Zero Social Scrolling">
              <div className="text-[11px] text-zinc-300 leading-relaxed">
                Phones live in the bag during the school day. Any social-media scrolling on campus results in a <strong className="text-red-400">1-month campus phone loss</strong>. Emergencies are routed through the front desk and margaret@prsvr.com.
              </div>
              <Checkbox label="I understand and enforce the campus device policy." value={form.device_policy_ack} onChange={(v) => update({ device_policy_ack: v })} test="ack-device" />
            </Card>

            <Card title="Baseline Diagnostics Schedule">
              <div className="text-[11px] text-zinc-400 leading-relaxed">
                Once orientation clears, your student will unlock <strong className="text-white">IXL Baseline Diagnostics</strong> (Math + ELA + Science + Social Studies) plus their initial <strong className="text-white">Athletic Target Metrics</strong> intake. A diagnostic-schedule badge will appear on their dashboard within 24 hours of study-code activation.
              </div>
            </Card>

            {/* Minor Concentration — per student */}
            <Card title="Minor Concentration · Elective Track">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                Every scholar-athlete pairs their core academic block with a <strong className="text-white">Minor Concentration</strong> — a semester-long elective track that becomes part of their student profile and 5-Star Roadmap.
              </div>
              <div className="mb-4">
                <div className="text-[10px] font-black tracking-widest text-amber-400 uppercase mb-1.5">
                  {form.student_first_name || 'Primary Student'} {form.student_last_name || ''} · Grade {form.grade || '?'}
                </div>
                <div className="grid md:grid-cols-4 gap-2">
                  {MINOR_OPTIONS.map((m) => {
                    const active = form.minor_concentration === m.id;
                    return (
                      <button key={m.id} type="button"
                              onClick={() => update({ minor_concentration: m.id })}
                              className={`text-left border-2 p-2.5 transition-colors ${active ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-800'}`}
                              data-testid={`minor-primary-${m.id}`}>
                        <div className="text-[11px] font-black text-white">{m.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              {form.siblings && form.siblings.map((s, i) => (
                <div key={`sib-minor-${i}`} className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="text-[10px] font-black tracking-widest text-amber-400 uppercase mb-1.5">
                    {s.student_first_name || `Sibling #${i+2}`} {s.student_last_name || ''} · Grade {s.grade || '?'}
                  </div>
                  <div className="grid md:grid-cols-4 gap-2">
                    {MINOR_OPTIONS.map((m) => {
                      const active = s.minor_concentration === m.id;
                      return (
                        <button key={m.id} type="button"
                                onClick={() => updateSibling(i, { minor_concentration: m.id })}
                                className={`text-left border-2 p-2.5 transition-colors ${active ? 'border-amber-500 bg-amber-500/10' : 'border-zinc-800'}`}
                                data-testid={`minor-sibling-${i}-${m.id}`}>
                          <div className="text-[11px] font-black text-white">{m.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </Card>

            {/* Locker Room Policy Grid — 4 quadrants from the Playbook */}
            <Card title="The Locker Room Policy Grid">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-3">Four quadrants govern life on campus. Read each — they show up daily.</div>
              <div className="grid md:grid-cols-2 gap-2">
                <PolicyQuad title="Communication · Code of Excellence" color="#C9A961" points={[
                  'Professional communication at all times',
                  "Honorifics: Sir, Ma'am, Mr., Mrs., Please, Thank You, I Apologize, Excuse Me",
                  'Cursing strictly prohibited',
                  'We are here to uplift, never to tear down',
                ]} />
                <PolicyQuad title="Technology & Biometrics" color="#22D3EE" points={[
                  'Biometric attendance — secure, tamper-proof',
                  'Phones stored in bags at all times unless authorized',
                  'ZERO social scrolling on campus',
                  'Violation = full month loss of campus phone privileges',
                ]} />
                <PolicyQuad title="Artificial Intelligence · Integrity" color="#10B981" points={[
                  'AI (Emergent.ai / Gemini) is a supplemental tool ONLY',
                  'Use for research + brainstorming, never as primary creator',
                  'Unauthorized AI-generated submissions = breach of Values',
                  'Consequence: immediate disciplinary review',
                ]} />
                <UniformQuadWithImage />
              </div>
            </Card>

            {/* 5-Star Athlete Profile Roadmap */}
            <Card title="The 5-Star Athlete Profile · Year-Long Roadmap">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-3">Every SAA student-athlete builds toward these four deliverables across the year — a complete recruit-ready profile by June.</div>
              <div className="grid md:grid-cols-2 gap-2">
                {FIVE_STAR_ROADMAP.map((q) => (
                  <div key={q.q} className="border-2 border-amber-500/30 bg-amber-500/5 p-3">
                    <div className="text-[9px] font-black tracking-widest text-amber-400 uppercase">{q.q}</div>
                    <div className="text-sm font-black text-white mt-1">{q.title}</div>
                    <div className="text-[11px] font-black" style={{ color: '#F97316' }}>DELIVERABLE · {q.deliverable}</div>
                    <div className="text-[10px] text-zinc-400 leading-snug mt-1.5">{q.focus}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* --------------- Q3 --------------- */}
        {step === 2 && (
          <div className="space-y-6" data-testid="q3-content">
            <SectionHeader color="#22D3EE" eyebrow="Quarter 3" title="Digital Environment · Tech Stack Configurator" desc="Confirm that every login is set up. SAA + IXL are required; the rest come online during the first week of school." />
            <Card title="Provision Your Student's Toolkit">
              <div className="grid md:grid-cols-2 gap-3">
                {TECH_STACK.map((t) => {
                  const Icon = t.icon;
                  const confirmed = form.tech_stack_confirmed.includes(t.id);
                  return (
                    <button key={t.id} type="button"
                            onClick={() => update({ tech_stack_confirmed: confirmed ? form.tech_stack_confirmed.filter(x => x !== t.id) : [...form.tech_stack_confirmed, t.id] })}
                            className={`text-left border-2 p-3 transition-colors ${confirmed ? 'border-cyan-500 bg-cyan-500/10' : 'border-zinc-800'}`}
                            data-testid={`tech-${t.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" style={{ color: '#22D3EE' }} />
                            <span className="text-sm font-black text-white">{t.label}</span>
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-1 leading-snug">{t.note}</div>
                          <a href={t.url} target={t.url.startsWith('http') ? '_blank' : undefined} rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] font-black tracking-widest text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 mt-2">
                            OPEN <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </div>
                        <div className={`w-5 h-5 border-2 flex items-center justify-center ${confirmed ? 'bg-cyan-500 border-cyan-500' : 'border-zinc-700'}`}>
                          {confirmed && <Check className="w-3 h-3 text-black" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-zinc-500 mt-3">Confirm at least 4 tools (SAA + IXL + Google Classroom + one more) to advance.</div>
            </Card>
          </div>
        )}

        {/* --------------- Q4 --------------- */}
        {step === 3 && (
          <div className="space-y-6" data-testid="q4-content">
            <SectionHeader color="#10B981" eyebrow="Quarter 4" title="Academic Lock & Study-Code Activation" desc="Final verification. Once you confirm, we issue the unique curriculum-block study code and email margaret@prsvr.com." />
            <Card title="Final Checklist">
              <ul className="space-y-2 text-[11px]">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> All 10 legal covenants initialed &amp; parent signature captured</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Uniform sizing submitted ({form.apparel_size || '—'})</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Culture covenant + device policy acknowledged</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Tech stack confirmed ({form.tech_stack_confirmed.length} tools)</li>
                <li className="flex items-center gap-2">{form.enrollment_fee_receipt_ref ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <span className="w-3.5 h-3.5 border border-amber-500 rounded-full" />} Enrollment fee ({form.enrollment_fee_receipt_ref ? 'receipt provided' : 'pending verification by admin'})</li>
              </ul>
              <Checkbox label="I confirm all information is accurate and I authorize SAA to issue the curriculum-block study code." value={form.all_forms_signed_ack} onChange={(v) => update({ all_forms_signed_ack: v })} test="final-ack" />
            </Card>

            <Card title="Absence & Travel Hotline">
              <div className="text-[11px] text-zinc-400 leading-relaxed mb-2">
                Pre-arranged athletic travel? Report early via the hotline so we can prep your student&rsquo;s async lesson pack.
              </div>
              <a href={`mailto:${config?.admin_email || 'margaret@prsvr.com'}?subject=Pre-Arranged Athletic Travel Absence`}
                 className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest text-emerald-400 hover:text-emerald-300 border border-emerald-500 px-3 py-1.5"
                 data-testid="absence-hotline">
                <Phone className="w-3 h-3" /> EMAIL THE ABSENCE HOTLINE
              </a>
            </Card>

            {result?.study_code && (
              <Card title="Curriculum Block Study Code · ISSUED">
                <div className="text-[10px] font-black tracking-[0.3em] text-emerald-400 uppercase mb-1">Your Student&rsquo;s Study Code</div>
                <div className="text-3xl md:text-4xl font-black text-white tracking-widest border-2 border-emerald-500 bg-emerald-500/5 p-3 text-center" data-testid="issued-study-code">{result.study_code}</div>
                <div className="text-[10px] text-zinc-500 mt-2 leading-relaxed">Log into the SAA Student Dashboard and paste this code to unlock your curriculum block. A confirmation has been sent to {config?.admin_email || 'margaret@prsvr.com'}.</div>
              </Card>
            )}
          </div>
        )}

        {/* --------------- Q5 --------------- */}
        {step === 4 && (
          <div className="space-y-6" data-testid="q5-content">
            {(() => {
              const allScholarship = familyTuition
                && familyTuition.students.length > 0
                && familyTuition.students.every((s) => s.plan_type === 'scholarship');
              return (
                <SectionHeader
                  color={allScholarship ? '#10B981' : '#A78BFA'}
                  eyebrow="Quarter 5"
                  title={familyTuition ? `Tuition Plans · ${familyTuition.family_label}` : 'Tuition Plan · Maddox Sterling'}
                  desc={allScholarship
                    ? "Your family is on a full-ride PRSVR Founder's Scholarship. Nothing is owed for the 2026-2027 academic year — the tiles below are for your records."
                    : "Choose a payment plan for each student. Selection is recorded in the parent ledger and margaret@prsvr.com queues the Sept 1 invoice in Stripe."}
                />
              );
            })()}
            <Card title="Billing Contact">
              <div className="grid md:grid-cols-2 gap-3">
                <Input label="Parent Email *" type="email" value={tuition.parent_email} onChange={(v) => setTuition({ ...tuition, parent_email: v })} test="tuition-parent-email" />
                <Input label="Billing Contact Email (if different)" type="email" value={tuition.billing_contact_email} onChange={(v) => setTuition({ ...tuition, billing_contact_email: v })} />
              </div>
            </Card>

            {/* ---- MULTI-STUDENT: render one card per student from familyTuition ---- */}
            {familyTuition && familyTuition.students.length > 0 ? (
              <>
                {familyTuition.students.map((stu) => {
                  const selectedPlan = studentSelections[stu.student_key] || null;
                  const recordedRes = studentSelections[`${stu.student_key}::result`];
                  const isOneTime = stu.plan_type === 'one_time';
                  const isScholarship = stu.plan_type === 'scholarship';

                  // ── Scholarship path — full-ride, $0 owed, no Stripe button ──
                  if (isScholarship) {
                    const meta = Object.values(stu.plans)[0] || {};
                    return (
                      <Card key={stu.student_key} title={`${stu.student_name} · Full-Ride Scholarship`}>
                        <div className="border-2 border-emerald-500 bg-emerald-500/5 p-5" data-testid={`scholarship-tile-${stu.student_key}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                              <Check className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[9px] font-black tracking-[0.32em] uppercase text-emerald-400">Tuition Award</div>
                              <div className="text-lg font-black text-white mt-0.5">{meta.label}</div>
                              <div className="mt-4 grid grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Standard Tuition</div>
                                  <div className="text-2xl font-black text-white line-through decoration-emerald-400/60">$8,000</div>
                                </div>
                                <div>
                                  <div className="text-[9px] uppercase tracking-widest font-black text-emerald-400">Family Pays</div>
                                  <div className="text-3xl font-black text-emerald-400">$0.00</div>
                                </div>
                              </div>
                              <div className="text-[11px] text-zinc-300 mt-4 leading-relaxed">{meta.schedule}</div>
                              <div className="text-[10px] text-zinc-500 mt-3 italic">No Stripe checkout · no invoice · nothing due on Sept 1, 2026.</div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  }

                  return (
                    <Card key={stu.student_key} title={`${stu.student_name} · ${isOneTime ? 'Final Payment' : 'Choose One Plan'}`}>
                      <div className={`grid ${isOneTime ? 'md:grid-cols-1' : Object.keys(stu.plans).length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
                        {Object.entries(stu.plans).map(([key, meta]) => {
                          const active = selectedPlan === key;
                          const hasLink = Boolean(meta.payment_link);
                          const badge = isOneTime ? 'One-Time Final Payment'
                                       : key === 'annual'   ? 'Maximum Savings'
                                       : key === 'biannual' ? 'Best 2-Payment Option'
                                       : key === 'quarterly'? 'Discounted Rate'
                                       : null;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setStudentSelections((prev) => ({ ...prev, [stu.student_key]: key }))}
                              className={`text-left border-2 p-4 transition-colors ${active ? 'border-purple-400 bg-purple-500/10' : 'border-zinc-800'}`}
                              data-testid={`tuition-${stu.student_key}-${key}`}>
                              {badge && <div className="text-[8px] font-black tracking-widest text-purple-300 mb-1">{badge.toUpperCase()}</div>}
                              <div className="text-xs font-black text-white">{meta.label}</div>
                              <div className="text-2xl font-black text-white mt-1">${meta.rate.toFixed(2)}</div>
                              <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mt-0.5">
                                {isOneTime         ? 'one-time'
                                 : key === 'monthly'   ? 'per month'
                                 : key === 'quarterly' ? 'per quarter'
                                 : key === 'biannual'  ? 'per 6 months'
                                 : 'one-time'}
                              </div>
                              <div className="text-[10px] text-zinc-400 mt-2 leading-snug">{meta.schedule}</div>
                              <div className={`text-[9px] font-black tracking-widest uppercase mt-2 ${hasLink ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {hasLink ? '✓ Instant Stripe Checkout Available' : '⏳ Invoice Sent Manually By Margaret'}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {recordedRes ? (
                        <div className="mt-4">
                          <div className="text-[10px] font-black tracking-widest text-emerald-400 uppercase">Tuition Confirmation · {recordedRes.record.student_name}</div>
                          <div className="mt-1 text-sm text-white">{recordedRes.record.plan.toUpperCase()} · ${recordedRes.record.rate.toFixed(2)} · starts Sept 1, 2026</div>
                          {(() => {
                            const chosen = stu.plans[recordedRes.record.plan];
                            const link = chosen?.payment_link;
                            if (link) {
                              const paymentUrl = `${link}${link.includes('?') ? '&' : '?'}prefilled_email=${encodeURIComponent(recordedRes.record.billing_contact_email)}`;
                              return (
                                <a href={paymentUrl} target="_blank" rel="noreferrer"
                                   className="mt-3 inline-flex items-center gap-2 text-xs font-black tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 px-4 py-2.5 uppercase"
                                   data-testid={`tuition-pay-now-${stu.student_key}`}>
                                  <HandCoins className="w-4 h-4" /> Pay Now · ${recordedRes.record.rate.toFixed(2)}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              );
                            }
                            return (
                              <div className="mt-3 text-[10px] text-amber-400 border border-amber-500/40 bg-amber-500/5 p-2">
                                Stripe invoice generation is queued for Margaret. She will send you the first invoice on Sept 1, 2026.
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={async () => {
                              const planKey = studentSelections[stu.student_key];
                              const res = await submitStudentTuition(stu.student_name, planKey);
                              if (res) {
                                setStudentSelections((prev) => ({ ...prev, [`${stu.student_key}::result`]: res }));
                              }
                            }}
                            disabled={!selectedPlan}
                            className="inline-flex items-center gap-2 text-xs font-black tracking-widest bg-purple-500 text-black px-4 py-2.5 hover:bg-purple-400 disabled:opacity-50 uppercase"
                            data-testid={`submit-tuition-${stu.student_key}`}>
                            <HandCoins className="w-4 h-4" /> Confirm {stu.student_name.split(' ')[0]}&rsquo;s Plan
                          </button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </>
            ) : (
              // ---- LEGACY SINGLE-STUDENT PATH (fallback if no familyTuition) ----
              <>
                <Card title="Choose One Plan (Tuition Due Sept 1, 2026)">
                  <div className="grid md:grid-cols-3 gap-3">
                    {Object.entries(config?.tuition_plans || {}).map(([key, meta]) => {
                      const active = tuition.plan === key;
                      const badge = key === 'annual' ? 'Maximum Savings' : key === 'quarterly' ? 'Discounted Rate' : null;
                      const hasLink = Boolean(meta.payment_link);
                      return (
                        <button key={key} type="button" onClick={() => setTuition({ ...tuition, plan: key })}
                                className={`text-left border-2 p-4 transition-colors ${active ? 'border-purple-400 bg-purple-500/10' : 'border-zinc-800'}`}
                                data-testid={`tuition-plan-${key}`}>
                          {badge && <div className="text-[8px] font-black tracking-widest text-purple-300 mb-1">{badge.toUpperCase()}</div>}
                          <div className="text-xs font-black text-white">{meta.label}</div>
                          <div className="text-2xl font-black text-white mt-1">${meta.rate.toFixed(2)}</div>
                          <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mt-0.5">{key === 'monthly' ? 'per month' : key === 'quarterly' ? 'per quarter' : 'one-time'}</div>
                          <div className="text-[10px] text-zinc-400 mt-2 leading-snug">{meta.schedule}</div>
                          <div className={`text-[9px] font-black tracking-widest uppercase mt-2 ${hasLink ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {hasLink ? '✓ Instant Stripe Checkout Available' : '⏳ Invoice Sent Manually By Margaret'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Card>

                {tuitionResult ? (
                  <Card title="Plan Recorded ✓">
                    <div className="text-[10px] font-black tracking-widest text-emerald-400 uppercase">Tuition Confirmation · {tuitionResult.record.student_name}</div>
                    <div className="mt-1 text-sm text-white">{tuitionResult.record.plan.toUpperCase()} · ${tuitionResult.record.rate.toFixed(2)} · starts Sept 1, 2026</div>
                    <div className="text-[10px] text-zinc-400 mt-1">A confirmation email was queued to {tuitionResult.record.billing_contact_email} and margaret@prsvr.com.</div>
                    {(() => {
                      const link = config?.tuition_plans?.[tuitionResult.record.plan]?.payment_link;
                      if (link) {
                        const paymentUrl = `${link}${link.includes('?') ? '&' : '?'}prefilled_email=${encodeURIComponent(tuitionResult.record.billing_contact_email)}`;
                        return (
                          <a href={paymentUrl} target="_blank" rel="noreferrer"
                             className="mt-3 inline-flex items-center gap-2 text-xs font-black tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 px-4 py-2.5 uppercase"
                             data-testid="tuition-pay-now">
                            <HandCoins className="w-4 h-4" /> Pay Now · ${tuitionResult.record.rate.toFixed(2)}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        );
                      }
                      return (
                        <div className="mt-3 text-[10px] text-amber-400 border border-amber-500/40 bg-amber-500/5 p-2">
                          Stripe invoice generation is queued for Margaret. She will send you the first invoice on Sept 1, 2026.
                        </div>
                      );
                    })()}
                  </Card>
                ) : (
                  <div className="flex justify-end">
                    <button onClick={submitTuition} disabled={tuitionSubmitting || !tuition.plan || !tuition.parent_email}
                            className="inline-flex items-center gap-2 text-xs font-black tracking-widest bg-purple-500 text-black px-4 py-2.5 hover:bg-purple-400 disabled:opacity-50 uppercase" data-testid="submit-tuition">
                      {tuitionSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
                      Confirm Plan & Queue Invoice
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Nav */}
        {step < 4 && (
          <div className="flex items-center justify-between mt-8 sticky bottom-0 bg-black py-3 border-t border-zinc-900">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                    className="inline-flex items-center gap-1 text-[10px] font-black tracking-widest text-zinc-400 hover:text-white disabled:opacity-30" data-testid="orientation-back">
              <ArrowLeft className="w-3 h-3" /> BACK
            </button>
            {step < 3 ? (
              <button onClick={() => setStep((s) => Math.min(3, s + 1))}
                      className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest text-black bg-amber-500 hover:bg-amber-400 px-4 py-2 uppercase"
                      data-testid="orientation-next">
                Continue <ArrowRight className="w-3 h-3" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !canAdvance}
                      className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest text-black bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2.5 uppercase"
                      data-testid="orientation-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Submit Orientation Packet
              </button>
            )}
          </div>
        )}

        {/* --------------- Q6 · School of Record ---------------
            The final orientation moment: is SOLO going to be this student's
            official school of record with the state (SAA autonomous school),
            or are they using SOLO alongside a different school-of-record
            (charter, private, homeschool)? This drives downstream CALPADS /
            attendance reporting and unlocks the "official transcripts"
            module vs. the "supplemental resource" mode. */}
        {step === 5 && (
          <div className="space-y-6" data-testid="q6-content">
            <SectionHeader
              color="#FFC72C"
              eyebrow="Quarter 6"
              title="School of Record"
              desc="The last decision. Two choices, no wrong answer — both work with SOLO. We just need to know which mode to run in for your family."
            />

            <div className="grid md:grid-cols-2 gap-4">
              {[
                {
                  key: true,
                  label: 'YES — Enroll SOLO as our school of record',
                  tag: 'Full autonomous school',
                  body: 'SAA becomes the official school for state records. We handle CALPADS reporting, attendance, official transcripts, and standardized-test administration. Your student receives an SAA diploma at graduation.',
                  color: '#FFC72C',
                  testid: 'solo-school-of-record-yes',
                },
                {
                  key: false,
                  label: 'NO — We keep our current school of record',
                  tag: 'SOLO as supplement',
                  body: 'Your student stays enrolled at their current charter / private / district school. SOLO runs as an advanced supplemental program — daily lessons, coaching, electives, athlete IQ — with no official reporting to the state.',
                  color: '#22D3EE',
                  testid: 'solo-school-of-record-no',
                },
              ].map((opt) => {
                const selected = form.solo_school_of_record === opt.key;
                return (
                  <button
                    key={String(opt.key)}
                    type="button"
                    onClick={async () => {
                      // Optimistic UI first so the demo feels instant.
                      update({
                        solo_school_of_record: opt.key,
                        solo_school_of_record_signed_at: new Date().toISOString(),
                      });
                      // Then persist. If the persist fails (e.g. offline),
                      // roll back so the user knows to try again.
                      try {
                        await api.post('/academy/orientation-portal/school-of-record', {
                          parent_email: form.parent_email,
                          solo_school_of_record: opt.key,
                        });
                      } catch (err) {
                        update({ solo_school_of_record: null, solo_school_of_record_signed_at: null });
                        toast.error('Could not save your choice — please try again');
                      }
                    }}
                    data-testid={opt.testid}
                    className="text-left p-6 border-2 transition-all hover:-translate-y-0.5"
                    style={{
                      background: selected ? '#0B1220' : '#000',
                      borderColor: selected ? opt.color : '#27272A',
                    }}
                  >
                    <div className="text-[10px] font-black uppercase tracking-[0.28em] mb-2" style={{ color: opt.color }}>
                      {opt.tag}
                    </div>
                    <div className="text-lg font-black leading-tight mb-3">{opt.label}</div>
                    <div className="text-xs leading-relaxed text-zinc-400">{opt.body}</div>
                    {selected && (
                      <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest" style={{ color: opt.color }}>
                        <Check className="w-3 h-3" /> Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {form.solo_school_of_record !== null && (
              <div className="mt-6 p-5 border-2 border-emerald-500/50 bg-emerald-500/5">
                <div className="text-[10px] font-black uppercase tracking-[0.28em] text-emerald-400 mb-2">
                  Locked In
                </div>
                <div className="text-sm text-white font-bold mb-1">
                  {form.solo_school_of_record
                    ? 'Welcome to Scholar Athlete Academy — your student\'s new school of record.'
                    : 'SOLO will run as your student\'s supplemental program alongside their current school.'}
                </div>
                <div className="text-xs text-zinc-400 leading-relaxed">
                  Margaret will follow up within 24 hours with the paperwork specific to your choice
                  ({form.solo_school_of_record ? 'affidavit + transcript request from prior school' : 'no additional paperwork'}).
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- small UI primitives ----------

const SectionHeader = ({ color, eyebrow, title, desc }) => (
  <div>
    <div className="text-[10px] font-black tracking-[0.32em] uppercase" style={{ color }}>{eyebrow}</div>
    <h2 className="text-xl md:text-3xl font-black mt-1 text-white">{title}</h2>
    <p className="text-[11px] text-zinc-400 leading-relaxed mt-1 max-w-3xl">{desc}</p>
  </div>
);

const Card = ({ title, children }) => (
  <div className="border-2 border-zinc-900 bg-zinc-950/40 p-4">
    <div className="text-[10px] font-black tracking-[0.25em] uppercase text-white mb-3">{title}</div>
    {children}
  </div>
);

const Input = ({ label, value, onChange, type = 'text', placeholder = '', test }) => (
  <label className="block">
    <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase mb-1">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
           className="w-full bg-black border border-zinc-800 focus:border-amber-500 outline-none p-2 text-sm text-white"
           data-testid={test} />
  </label>
);
const TextArea = ({ label, value, onChange, rows = 2 }) => (
  <label className="block mt-2">
    <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase mb-1">{label}</div>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
              className="w-full bg-black border border-zinc-800 focus:border-amber-500 outline-none p-2 text-sm text-white" />
  </label>
);
const Select = ({ label, value, onChange, options, test }) => (
  <label className="block">
    <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase mb-1">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={test}
            className="w-full bg-black border border-zinc-800 focus:border-amber-500 outline-none p-2 text-sm text-white">
      {options.map((o) => <option key={o} value={o}>{o || '— Select —'}</option>)}
    </select>
  </label>
);
const Checkbox = ({ label, value, onChange, test }) => (
  <label className="mt-3 flex items-start gap-2 cursor-pointer">
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 accent-amber-500" data-testid={test} />
    <span className="text-[11px] text-zinc-300 leading-relaxed">{label}</span>
  </label>
);
const UniformQuadWithImage = () => (
  <div className="border-2 p-2.5 md:col-span-2" style={{ borderColor: '#F9731655', background: '#F9731608' }}>
    <div className="text-[10px] font-black tracking-widest uppercase" style={{ color: '#F97316' }}>Uniforms · Professionalism</div>
    <div className="grid md:grid-cols-[160px_1fr] gap-3 mt-2 items-start">
      <div>
        <img src="/saa-uniform-polo.jpg" alt="SAA Official Polo" className="w-full border border-orange-500/40" data-testid="saa-uniform-image" />
        <div className="text-[8px] font-black tracking-widest text-orange-400/70 uppercase text-center mt-1">SAA · Official Polo</div>
      </div>
      <ul className="space-y-1">
        {[
          'PRSVR / SAA / HandleLife gear required during school hours',
          'PRSVR Polo mandatory on designated academic days',
          'Uniform violation = immediate athletic consequence',
          'You represent an elite brand — present accordingly',
        ].map((p, i) => (
          <li key={i} className="text-[10px] text-zinc-300 leading-snug flex gap-1.5">
            <span style={{ color: '#F97316' }}>▸</span><span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const PolicyQuad = ({ title, color, points }) => (
  <div className="border-2 p-2.5" style={{ borderColor: `${color}55`, background: `${color}0A` }}>
    <div className="text-[10px] font-black tracking-widest uppercase" style={{ color }}>{title}</div>
    <ul className="mt-1.5 space-y-1">
      {points.map((p, i) => (
        <li key={i} className="text-[10px] text-zinc-300 leading-snug flex gap-1.5">
          <span style={{ color }}>▸</span><span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);
const CovenantInitial = ({ n, title, body, initials, onChange, test, image }) => (
  <div className="border border-zinc-900 p-2.5 mb-2">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <div className="text-[9px] font-black tracking-widest text-amber-400 uppercase">Covenant {n}</div>
        <div className="text-xs font-black text-white mt-0.5">{title}</div>
        <div className="text-[10px] text-zinc-400 leading-relaxed mt-1">{body}</div>
        {image && (
          <div className="mt-2 flex items-center gap-2">
            <img src={image.src} alt={image.caption} className="w-16 h-16 object-cover border border-orange-500/40" />
            <div className="text-[9px] font-black tracking-widest text-orange-400 uppercase">{image.caption}</div>
          </div>
        )}
      </div>
      <div className="w-20 shrink-0">
        <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase mb-1 text-center">Initials</div>
        <input value={initials} onChange={(e) => onChange(e.target.value)} maxLength={5}
               className="w-full bg-black border-2 border-amber-500/60 focus:border-amber-500 outline-none p-2 text-center text-sm text-amber-400 font-black tracking-widest"
               data-testid={test} placeholder="___" />
      </div>
    </div>
  </div>
);

export default Orientation;
